// src/web/client/src/components/activity/activityLogModel.js
//
// Pure helpers for the Activity log: turn this app's sent messages and the
// brain's timeline into ONE list of entries, group them by day, and collapse
// runs of identical lines ("D. Levi opened an email" ×5 → one row with a count
// and a time span). Pure + exported for the tests.

export const RANGES = [
  { value: '7d', label: 'Last 7 days', days: 7, platform: '7d', messages: '7' },
  { value: '30d', label: 'Last 30 days', days: 30, platform: '30d', messages: '30' },
  { value: '90d', label: 'Last 90 days', days: 90, platform: '30d', messages: '90' },
];
export const KINDS = [
  { value: 'all', label: 'Everything' },
  { value: 'emails', label: 'Emails sent' },
  { value: 'restock', label: 'Restock reminders', platform: 'reorder' },
  { value: 'reviews', label: 'Review requests', platform: 'review' },
  { value: 'loyalty', label: 'Loyalty', platform: 'loyalty' },
  { value: 'engagement', label: 'Opens & clicks', platform: 'engagement' },
];

const TYPE_LABEL = { thank_you: 'thank-you email', review_request: 'review request', vip_upsell: 'VIP email', custom_offer: 'custom offer', follow_up: 'follow-up', win_back: 'win-back email' };

// One of this app's Message rows → an entry.
export function entryFromMessage(m) {
  const at = m.sent_date || m.createdAt || m.updatedAt || null;
  const name = m.customer_name || m.customer_email || 'a customer';
  const what = TYPE_LABEL[m.type] || 'email';
  const verb = m.status === 'sent' ? 'Sent' : m.status === 'scheduled' ? 'Scheduled' : 'Prepared';
  const extras = [m.opened ? 'opened' : null, m.clicked ? 'clicked' : null].filter(Boolean);
  return {
    id: `m:${m._id}`,
    at,
    kind: 'emails',
    label: `${verb} ${name} a ${what}`,
    detail: extras.length ? extras.join(' · ') : null,
    severity: m.status === 'failed' ? 'error' : 'info',
    message: m,
  };
}

// One of the brain's timeline items → an entry (the Platform groups by day already;
// we flatten, then regroup with ours).
export function entryFromPlatformItem(item, i) {
  const kindMap = { replenish: 'restock', reviews: 'reviews', loyalty: 'loyalty', retain: 'engagement' };
  return {
    id: `p:${item.at}:${i}`,
    at: item.at,
    kind: kindMap[item.kind] || 'engagement',
    label: item.label,
    detail: null,
    severity: item.severity || 'info',
    message: null,
  };
}

const dayKey = (at) => new Date(at).toISOString().slice(0, 10);

// Collapse consecutive entries (newest first, already sorted) that have the SAME
// label within a day: one row, `count`, and the span [last…first].
export function collapseRuns(entries) {
  const out = [];
  for (const e of entries) {
    const prev = out[out.length - 1];
    if (prev && prev.label === e.label && prev.kind === e.kind && dayKey(prev.at) === dayKey(e.at) && !e.message && !prev.message) {
      prev.count += 1;
      prev.from = e.at; // older end of the run
    } else {
      out.push({ ...e, count: 1, from: e.at, to: e.at });
    }
  }
  return out;
}

// Merge, filter by kind, sort newest first, collapse runs, group by day.
export function buildDays({ messages = [], platformDays = [], kind = 'all' }) {
  const entries = [
    ...messages.map(entryFromMessage),
    ...platformDays.flatMap((d) => (d.items || []).map(entryFromPlatformItem)),
  ].filter((e) => e.at && (kind === 'all' || e.kind === kind));
  entries.sort((a, b) => new Date(b.at) - new Date(a.at));
  const collapsed = collapseRuns(entries);
  const map = new Map();
  for (const e of collapsed) {
    const k = dayKey(e.at);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(e);
  }
  return [...map.entries()].map(([date, items]) => ({ date, items, total: items.reduce((s, i) => s + i.count, 0) }));
}

export function dayLabel(dateStr) {
  const today = new Date().toISOString().slice(0, 10);
  const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (dateStr === today) return 'Today';
  if (dateStr === y) return 'Yesterday';
  return new Date(`${dateStr}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}
export const timeOf = (at) => new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
